/**
 * 행·열 구조 편집(삽입/삭제)과 복사/채우기 시의 좌표·수식 참조 보정 순수 모듈.
 *
 * 세 적용기(ExcelRenderer 인터랙티브 편집 / docAgent applyPatch.ts /
 * ai xlsx `_structural_shift.py` 미러)가 이 계약을 공유한다. Python 미러와는
 * `structuralShift.vectors.json` 공유 벡터로 동작 일치를 CI 에서 검증한다.
 *
 * 좌표 규약: 모든 인덱스·pivot 은 **0-based**. 1-based IR(A1 키, rowIndex 등)
 * 을 다루는 호출부가 변환 책임을 진다.
 *
 * 두 가지 참조 보정 시맨틱을 명확히 구분한다(실제 Excel 과 동일):
 * - `rewriteFormulaRefs` — 구조 편집(행·열 삽입/삭제). `$` 절대 표기와 무관하게
 *   pivot 이후 참조가 이동하고, 삭제 범위를 가리키던 참조는 `#REF!` 가 된다.
 *   시트 접두 참조(`Sheet2!A1`)는 다른 시트 소속이므로 건드리지 않는다.
 * - `rebaseFormula` — 복사/붙여넣기/채우기. `$` 로 고정되지 않은 상대 참조만
 *   (dRow, dCol) 만큼 이동하고, 음수 좌표가 되면 `#REF!` 가 된다. 시트 접두
 *   참조도 로컬 A1 부분은 이동한다.
 *
 * 파싱이 불확실한 토큰은 절대 변형하지 않는다(fail-open) — 수식을 깨뜨리는
 * 것보다 보정을 포기하는 쪽이 항상 안전하다.
 */

export type ShiftAxis = 'row' | 'col';

export interface GridRect {
  minR: number;
  maxR: number;
  minC: number;
  maxC: number;
}

export const REF_ERROR = '#REF!';

/* ── A1 좌표 헬퍼 ─────────────────────────────────────────────── */

export const colLettersToIndex = (letters: string): number => {
  let n = 0;
  const up = letters.toUpperCase();
  for (let i = 0; i < up.length; i++) n = n * 26 + (up.charCodeAt(i) - 64);
  return n - 1;
};

export const colIndexToLetters = (idx: number): string => {
  let n = idx + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

export const parseA1Cell = (a1: string): { row: number; col: number } | null => {
  const m = /^\$?([A-Za-z]{1,3})\$?(\d+)$/.exec(a1.trim());
  if (!m) return null;
  const row = parseInt(m[2], 10) - 1;
  if (row < 0) return null;
  return { row, col: colLettersToIndex(m[1]) };
};

export const a1FromRowCol = (row: number, col: number): string =>
  `${colIndexToLetters(col)}${row + 1}`;

/** "A1" | "A1:C5" → GridRect (단일 셀은 1×1). 파싱 불가 시 null. */
export const parseA1Range = (range: string): GridRect | null => {
  const parts = range.trim().split(':');
  if (parts.length > 2) return null;
  const first = parseA1Cell(parts[0]);
  if (!first) return null;
  const second = parts.length === 2 ? parseA1Cell(parts[1]) : first;
  if (!second) return null;
  return {
    minR: Math.min(first.row, second.row),
    maxR: Math.max(first.row, second.row),
    minC: Math.min(first.col, second.col),
    maxC: Math.max(first.col, second.col),
  };
};

export const rectToA1Range = (rect: GridRect): string =>
  `${a1FromRowCol(rect.minR, rect.minC)}:${a1FromRowCol(rect.maxR, rect.maxC)}`;

/* ── 인덱스/사각형 시프트 ─────────────────────────────────────── */

/**
 * 구조 편집에 따른 단일 인덱스 이동.
 * 삽입: delta = +count (pivot 위치에 count 개 삽입 → pivot 이상은 +count).
 * 삭제: delta = -count (pivot 부터 count 개 제거 → 삭제 구간 내부는 null).
 */
export const shiftIndex = (idx: number, pivot: number, delta: number): number | null => {
  if (idx < pivot) return idx;
  if (delta >= 0) return idx + delta;
  if (idx < pivot - delta) return null; // pivot - delta === pivot + count
  return idx + delta;
};

/**
 * 사각 영역의 축 방향 시프트/신축.
 * 삽입: pivot ≤ min → 전체 이동, min < pivot ≤ max → 확장(Excel 과 동일).
 * 삭제: 부분 겹침은 축소, 전부 삭제되면 null.
 */
export const shiftRect = (
  rect: GridRect,
  axis: ShiftAxis,
  pivot: number,
  delta: number,
): GridRect | null => {
  const lo = axis === 'row' ? rect.minR : rect.minC;
  const hi = axis === 'row' ? rect.maxR : rect.maxC;
  let nLo: number;
  let nHi: number;
  if (delta >= 0) {
    nLo = pivot <= lo ? lo + delta : lo;
    nHi = pivot <= hi ? hi + delta : hi;
  } else {
    const cutEnd = pivot - delta; // pivot + count
    nLo = lo < pivot ? lo : lo >= cutEnd ? lo + delta : pivot;
    nHi = hi < pivot ? hi : hi >= cutEnd ? hi + delta : pivot - 1;
    if (nHi < nLo) return null;
  }
  return axis === 'row'
    ? { ...rect, minR: nLo, maxR: nHi }
    : { ...rect, minC: nLo, maxC: nHi };
};

/**
 * A1 셀 키("B12")의 구조 시프트. 삭제 구간에 들어가면 null,
 * 파싱 불가능한 키는 fail-open 으로 원문 그대로 반환한다.
 */
export const shiftA1Key = (
  a1: string,
  axis: ShiftAxis,
  pivot: number,
  delta: number,
): string | null => {
  const cell = parseA1Cell(a1);
  if (!cell) return a1;
  const v = axis === 'row' ? cell.row : cell.col;
  const nv = shiftIndex(v, pivot, delta);
  if (nv === null) return null;
  return axis === 'row' ? a1FromRowCol(nv, cell.col) : a1FromRowCol(cell.row, nv);
};

/* ── 수식 참조 스캐너 ─────────────────────────────────────────── */

interface RefEndpoint {
  colAbs: boolean;
  col: number | null; // colOnly/cell 에만 존재
  rowAbs: boolean;
  row: number | null; // rowOnly/cell 에만 존재
  text: string; // 원문 (변경 없을 때 그대로 재사용)
}

type RefKind = 'cell' | 'colOnly' | 'rowOnly';

interface ParsedRef {
  kind: RefKind;
  start: RefEndpoint;
  end: RefEndpoint | null; // 범위(A1:B2, A:B, 1:5)의 두 번째 끝점
  sheetPrefix: string | null; // "Sheet1!" / "'My Sheet'!" (원문, ! 포함)
}

/** ref 를 대체할 새 문자열, 또는 null(변경 없음 — 원문 유지). */
type RefMapper = (ref: ParsedRef) => string | null;

const CELL_RE = /^(\$?)([A-Za-z]{1,3})(\$?)(\d+)$/;
const COL_ONLY_RE = /^(\$?)([A-Za-z]{1,3})$/;
const ROW_ONLY_RE = /^(\$?)(\d+)$/;
const RUN_CHAR_RE = /[A-Za-z0-9_.$]/;

const parseEndpoint = (token: string): { kind: RefKind; ep: RefEndpoint } | null => {
  let m = CELL_RE.exec(token);
  if (m) {
    const row = parseInt(m[4], 10) - 1;
    if (row < 0) return null;
    return {
      kind: 'cell',
      ep: {
        colAbs: m[1] === '$',
        col: colLettersToIndex(m[2]),
        rowAbs: m[3] === '$',
        row,
        text: token,
      },
    };
  }
  m = COL_ONLY_RE.exec(token);
  if (m) {
    return {
      kind: 'colOnly',
      ep: { colAbs: m[1] === '$', col: colLettersToIndex(m[2]), rowAbs: false, row: null, text: token },
    };
  }
  m = ROW_ONLY_RE.exec(token);
  if (m) {
    const row = parseInt(m[2], 10) - 1;
    if (row < 0) return null;
    return {
      kind: 'rowOnly',
      ep: { colAbs: false, col: null, rowAbs: m[1] === '$', row, text: token },
    };
  }
  return null;
};

const endpointText = (kind: RefKind, ep: RefEndpoint): string => {
  if (kind === 'cell') {
    return `${ep.colAbs ? '$' : ''}${colIndexToLetters(ep.col as number)}${ep.rowAbs ? '$' : ''}${(ep.row as number) + 1}`;
  }
  if (kind === 'colOnly') return `${ep.colAbs ? '$' : ''}${colIndexToLetters(ep.col as number)}`;
  return `${ep.rowAbs ? '$' : ''}${(ep.row as number) + 1}`;
};

const readRun = (s: string, from: number): number => {
  let j = from;
  while (j < s.length && RUN_CHAR_RE.test(s[j])) j++;
  return j;
};

/** i 위치의 run 토큰(+선택적 `:` 범위 연속)을 참조로 파싱해 매핑을 시도한다. */
const consumeRef = (
  s: string,
  i: number,
  sheetPrefix: string | null,
  mapRef: RefMapper,
): { text: string; next: number } => {
  const j = readRun(s, i);
  const t1 = s.slice(i, j);
  // 함수 호출(LOG10( 등)은 절대 변형하지 않는다
  if (s[j] === '(') return { text: (sheetPrefix ?? '') + t1, next: j };
  const p1 = parseEndpoint(t1);
  if (!p1) return { text: (sheetPrefix ?? '') + t1, next: j };

  // 범위 연속 시도: `t1:t2` (양끝 동종일 때만 범위로 인정)
  if (s[j] === ':') {
    const k = readRun(s, j + 1);
    const t2 = s.slice(j + 1, k);
    const p2 = t2 ? parseEndpoint(t2) : null;
    if (p2 && p2.kind === p1.kind && s[k] !== '(') {
      const ref: ParsedRef = { kind: p1.kind, start: p1.ep, end: p2.ep, sheetPrefix };
      const mapped = mapRef(ref);
      const original = (sheetPrefix ?? '') + t1 + ':' + t2;
      return { text: mapped === null ? original : mapped, next: k };
    }
  }

  // 단일 끝점: cell 만 참조로 취급 (bare `A` / bare `12` 는 참조가 아님)
  if (p1.kind !== 'cell') return { text: (sheetPrefix ?? '') + t1, next: j };
  const ref: ParsedRef = { kind: 'cell', start: p1.ep, end: null, sheetPrefix };
  const mapped = mapRef(ref);
  return { text: mapped === null ? (sheetPrefix ?? '') + t1 : mapped, next: j };
};

/**
 * 수식 문자열을 스캔하며 셀/범위 참조에만 mapper 를 적용한다.
 * 문자열 리터럴("...", "" 이스케이프)과 함수명은 건너뛴다.
 * 시트 접두(따옴표 포함)는 mapper 에 원문으로 전달된다.
 */
export const mapFormulaRefs = (formula: string, mapRef: RefMapper): string => {
  let out = '';
  let i = 0;
  const n = formula.length;
  while (i < n) {
    const ch = formula[i];
    if (ch === '"') {
      let j = i + 1;
      while (j < n) {
        if (formula[j] === '"') {
          if (formula[j + 1] === '"') {
            j += 2;
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      out += formula.slice(i, j);
      i = j;
      continue;
    }
    if (ch === "'") {
      let j = i + 1;
      while (j < n) {
        if (formula[j] === "'") {
          if (formula[j + 1] === "'") {
            j += 2;
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      if (formula[j] === '!') {
        const consumed = consumeRef(formula, j + 1, formula.slice(i, j + 1), mapRef);
        out += consumed.text;
        i = consumed.next;
      } else {
        out += formula.slice(i, j);
        i = j;
      }
      continue;
    }
    if (RUN_CHAR_RE.test(ch)) {
      const j = readRun(formula, i);
      if (formula[j] === '!') {
        // 비따옴표 시트 접두 (Sheet1!A1) — `#REF!` 의 `REF!` 처럼 뒤에 참조가
        // 없으면 consumeRef 가 fail-open 으로 원문을 보존한다.
        const consumed = consumeRef(formula, j + 1, formula.slice(i, j + 1), mapRef);
        out += consumed.text;
        i = consumed.next;
        continue;
      }
      const consumed = consumeRef(formula, i, null, mapRef);
      out += consumed.text;
      i = consumed.next;
      continue;
    }
    out += ch;
    i++;
  }
  return out;
};

const formulaSheetNameFromPrefix = (prefix: string): string => {
  const withoutBang = prefix.endsWith('!') ? prefix.slice(0, -1) : prefix;
  if (withoutBang.startsWith("'") && withoutBang.endsWith("'")) {
    return withoutBang.slice(1, -1).replace(/''/g, "'");
  }
  return withoutBang;
};

const formulaSheetPrefix = (sheetName: string): string => {
  // Quote conservatively when Excel could parse the name as an address/name,
  // or when the scanner cannot safely consume it as one unquoted run.
  const safeIdentifier = /^[A-Za-z_][A-Za-z0-9_.]*$/.test(sheetName);
  const addressLike = /^\$?[A-Za-z]{1,3}\$?\d+$/.test(sheetName)
    || /^R\d+C\d+$/i.test(sheetName);
  if (safeIdentifier && !addressLike) return `${sheetName}!`;
  return `'${sheetName.replace(/'/g, "''")}'!`;
};

/**
 * Rewrite references to one renamed worksheet while leaving string literals,
 * other sheets, A1 spelling, absolute markers, and formula operators intact.
 * Sheet-name matching follows Excel's case-insensitive identity rules.
 */
export const renameSheetReferencesInFormula = (
  formula: string,
  oldSheetName: string,
  nextSheetName: string,
): string => {
  if (!formula || oldSheetName === nextSheetName) return formula;
  const oldKey = oldSheetName.toLocaleLowerCase('en-US');
  const nextPrefix = formulaSheetPrefix(nextSheetName);
  return mapFormulaRefs(formula, (ref) => {
    if (ref.sheetPrefix === null) return null;
    const referencedName = formulaSheetNameFromPrefix(ref.sheetPrefix);
    if (referencedName.toLocaleLowerCase('en-US') !== oldKey) return null;
    const reference = ref.end === null
      ? ref.start.text
      : `${ref.start.text}:${ref.end.text}`;
    return `${nextPrefix}${reference}`;
  });
};

/* ── 구조 편집용 참조 재작성 ($ 무시, 시트 접두 제외) ─────────── */

const rewriteFormulaRefsMatching = (
  formula: string,
  axis: ShiftAxis,
  pivot: number,
  delta: number,
  shouldShift: (ref: ParsedRef) => boolean,
): string => mapFormulaRefs(formula, (ref) => {
    if (!shouldShift(ref)) return null;
    if (ref.kind === 'colOnly' && axis === 'row') return null;
    if (ref.kind === 'rowOnly' && axis === 'col') return null;
    const prefix = ref.sheetPrefix ?? '';

    const coordOf = (ep: RefEndpoint): number => (axis === 'row' ? (ep.row as number) : (ep.col as number));
    const withCoord = (ep: RefEndpoint, v: number): RefEndpoint =>
      axis === 'row' ? { ...ep, row: v } : { ...ep, col: v };

    if (ref.end === null) {
      const nv = shiftIndex(coordOf(ref.start), pivot, delta);
      if (nv === null) return `${prefix}${REF_ERROR}`;
      if (nv === coordOf(ref.start)) return null;
      return `${prefix}${endpointText(ref.kind, withCoord(ref.start, nv))}`;
    }

    // 범위: 축 좌표를 lo/hi 로 정규화해 시프트/신축
    const v1 = coordOf(ref.start);
    const v2 = coordOf(ref.end);
    const lo = Math.min(v1, v2);
    const hi = Math.max(v1, v2);
    let nLo: number;
    let nHi: number;
    if (delta >= 0) {
      nLo = pivot <= lo ? lo + delta : lo;
      nHi = pivot <= hi ? hi + delta : hi;
    } else {
      const cutEnd = pivot - delta;
      nLo = lo < pivot ? lo : lo >= cutEnd ? lo + delta : pivot;
      nHi = hi < pivot ? hi : hi >= cutEnd ? hi + delta : pivot - 1;
      if (nHi < nLo) return `${prefix}${REF_ERROR}`;
    }
    if (nLo === lo && nHi === hi) return null;
    const startV = v1 <= v2 ? nLo : nHi;
    const endV = v1 <= v2 ? nHi : nLo;
    return prefix + (
      endpointText(ref.kind, withCoord(ref.start, startV)) +
      ':' +
      endpointText(ref.kind, withCoord(ref.end, endV))
    );
  });

export const rewriteFormulaRefs = (
  formula: string,
  axis: ShiftAxis,
  pivot: number,
  delta: number,
): string => rewriteFormulaRefsMatching(
  formula,
  axis,
  pivot,
  delta,
  (ref) => ref.sheetPrefix === null,
);

/**
 * Workbook-aware structural rewrite. Unqualified references belong to the
 * formula owner's sheet; qualified references belong to their explicit sheet.
 * Only references resolving to the structurally edited target are shifted.
 */
export const rewriteFormulaRefsForSheetShift = (
  formula: string,
  ownerSheetName: string,
  targetSheetName: string,
  axis: ShiftAxis,
  pivot: number,
  delta: number,
): string => {
  if (!formula || !Number.isInteger(pivot) || pivot < 0 || !Number.isInteger(delta) || delta === 0) {
    return formula;
  }
  const targetKey = targetSheetName.toLocaleLowerCase('en-US');
  const ownerIsTarget = ownerSheetName.toLocaleLowerCase('en-US') === targetKey;
  return rewriteFormulaRefsMatching(formula, axis, pivot, delta, (ref) => {
    if (ref.sheetPrefix === null) return ownerIsTarget;
    return formulaSheetNameFromPrefix(ref.sheetPrefix).toLocaleLowerCase('en-US') === targetKey;
  });
};

/* ── 복사/붙여넣기/채우기용 rebase ($ 존중, 시트 접두 포함) ────── */

export const rebaseFormula = (formula: string, dRow: number, dCol: number): string =>
  mapFormulaRefs(formula, (ref) => {
    const shiftEp = (ep: RefEndpoint): RefEndpoint | null => {
      let col = ep.col;
      let row = ep.row;
      if (col !== null && !ep.colAbs) {
        col = col + dCol;
        if (col < 0) return null;
      }
      if (row !== null && !ep.rowAbs) {
        row = row + dRow;
        if (row < 0) return null;
      }
      return { ...ep, col, row };
    };

    const ns = shiftEp(ref.start);
    const ne = ref.end === null ? undefined : shiftEp(ref.end);
    if (ns === null || ne === null) return (ref.sheetPrefix ?? '') + REF_ERROR;

    const changed =
      ns.col !== ref.start.col ||
      ns.row !== ref.start.row ||
      (ne !== undefined && ref.end !== null && (ne.col !== ref.end.col || ne.row !== ref.end.row));
    if (!changed) return null;

    const text =
      ne === undefined
        ? endpointText(ref.kind, ns)
        : `${endpointText(ref.kind, ns)}:${endpointText(ref.kind, ne)}`;
    return (ref.sheetPrefix ?? '') + text;
  });
